import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  PaginatedResponse,
  User,
  CreateUserDto,
  UpdateUserDto,
  UserQueryParams,
  Grant,
  CreateGrantDto,
  UpdateGrantDto,
  GrantQueryParams,
  GrantSummary,
  Transaction,
  TransactionListItem,
  CreateTransactionDto,
  CreateSplitExpenseDto,
  UpdateTransactionDto,
  TransactionQueryParams,
  MonthlySummary,
  UserBalance,
  BalanceSummary,
  UserBalanceDetail,
  PairwiseBalance,
  GrantUtilization,
  GrantReimbursement,
  Settlement,
  CreateSettlementDto,
  SettlementQueryParams,
  UserBalanceBreakdown,
  BalanceHistoryPoint,
  DashboardData,
  DashboardQueryParams,
  MonthlySpendingPoint,
  CategoryBreakdownItem,
  ExportQueryParams,
  UploadTokenRequest,
  UploadTokenResponse,
  ConfirmUploadDto,
  Receipt,
  BulkOperationResult,
  BulkCreateTransactionsRequest,
  BulkCreateResult,
  BulkEditTransactionsRequest,
  BulkEditResult,
} from '../types/api';

/**
 * API Configuration
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Create axios instance with base configuration
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

/**
 * Token getter function - will be set by AuthProvider
 */
let tokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the token getter function
 * Called by AuthProvider to inject the getAccessToken function
 */
export const setTokenGetter = (getter: () => Promise<string | null>): void => {
  tokenGetter = getter;
};

/**
 * Request interceptor to attach Authorization header
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    if (tokenGetter) {
      const token = await tokenGetter();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - token might be expired
    if (error.response?.status === 401 && originalRequest) {
      // Clear any cached token and trigger re-authentication
      console.warn('API: Received 401 Unauthorized');
      // The AuthProvider will handle re-authentication
    }

    // Handle network errors
    if (!error.response) {
      console.error('API: Network error', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Build query string from params object
 */
const buildQueryString = <T extends object>(params: T): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Health API
// ============================================================================

export const healthApi = {
  checkLiveness: () => apiClient.get('/health/live'),
  checkReadiness: () => apiClient.get('/health/ready'),
};

// ============================================================================
// Auth API
// ============================================================================

export const authApi = {
  getStatus: () => apiClient.get('/auth/status'),
  getProfile: () => apiClient.get('/auth/me'),
  validateToken: () => apiClient.get('/auth/validate'),
};

// ============================================================================
// Users API
// ============================================================================

export const usersApi = {
  /**
   * Get all users with pagination and filtering
   */
  getAll: (params?: UserQueryParams) => 
    apiClient.get<PaginatedResponse<User>>(`/users${buildQueryString(params || {})}`),

  /**
   * Get a user by ID
   */
  getById: (id: string) => 
    apiClient.get<ApiResponse<User>>(`/users/${id}`),

  /**
   * Create a new user
   */
  create: (data: CreateUserDto) => 
    apiClient.post<ApiResponse<User>>('/users', data),

  /**
   * Update a user
   */
  update: (id: string, data: UpdateUserDto) => 
    apiClient.put<ApiResponse<User>>(`/users/${id}`, data),

  /**
   * Delete a user (soft delete)
   */
  delete: (id: string) => 
    apiClient.delete<ApiResponse<User>>(`/users/${id}`),

  /**
   * Get active founders for dropdowns
   */
  getFounders: () => 
    apiClient.get<ApiResponse<User[]>>('/users/founders'),

  /**
   * Get active grant entities for dropdowns
   */
  getGrantEntities: () => 
    apiClient.get<ApiResponse<User[]>>('/users/grants'),
};

// ============================================================================
// Grants API
// ============================================================================

export const grantsApi = {
  /**
   * Get all grants with pagination and filtering
   */
  getAll: (params?: GrantQueryParams) => 
    apiClient.get<PaginatedResponse<Grant>>(`/grants${buildQueryString(params || {})}`),

  /**
   * Get a grant by ID
   */
  getById: (id: string) => 
    apiClient.get<ApiResponse<Grant>>(`/grants/${id}`),

  /**
   * Create a new grant
   */
  create: (data: CreateGrantDto) => 
    apiClient.post<ApiResponse<Grant>>('/grants', data),

  /**
   * Update a grant
   */
  update: (id: string, data: UpdateGrantDto) => 
    apiClient.put<ApiResponse<Grant>>(`/grants/${id}`, data),

  /**
   * Delete a grant (soft delete)
   */
  delete: (id: string) => 
    apiClient.delete<ApiResponse<Grant>>(`/grants/${id}`),

  /**
   * Get active grants for dropdowns
   */
  getActive: () => 
    apiClient.get<ApiResponse<Grant[]>>('/grants/active'),

  /**
   * Get grant summary with utilization
   */
  getSummary: (id: string) => 
    apiClient.get<ApiResponse<GrantSummary>>(`/grants/${id}/summary`),
};

// ============================================================================
// Transactions API
// ============================================================================

export const transactionsApi = {
  /**
   * Get all transactions with pagination and filtering
   */
  getAll: (params?: TransactionQueryParams) => 
    apiClient.get<PaginatedResponse<TransactionListItem>>(`/transactions${buildQueryString(params || {})}`),

  /**
   * Get a transaction by ID
   */
  getById: (id: string) => 
    apiClient.get<ApiResponse<Transaction>>(`/transactions/${id}`),

  /**
   * Create a new transaction (low-level with manual lines)
   */
  create: (data: CreateTransactionDto) => 
    apiClient.post<ApiResponse<Transaction>>('/transactions', data),

  /**
   * Create a split expense (high-level with automatic line generation)
   */
  createSplitExpense: (data: CreateSplitExpenseDto) => 
    apiClient.post<ApiResponse<Transaction>>('/transactions/split', data),

  /**
   * Update a transaction
   */
  update: (id: string, data: UpdateTransactionDto) => 
    apiClient.put<ApiResponse<Transaction>>(`/transactions/${id}`, data),

  /**
   * Delete a transaction
   */
  delete: (id: string) => 
    apiClient.delete(`/transactions/${id}`),

  /**
   * Get transactions by user
   */
  getByUser: (userId: string, params?: TransactionQueryParams) => 
    apiClient.get<PaginatedResponse<TransactionListItem>>(`/transactions/user/${userId}${buildQueryString(params || {})}`),

  /**
   * Get transactions by grant
   */
  getByGrant: (grantId: string, params?: TransactionQueryParams) => 
    apiClient.get<PaginatedResponse<TransactionListItem>>(`/transactions/grant/${grantId}${buildQueryString(params || {})}`),

  /**
   * Get monthly expense summary
   */
  getMonthlySummary: (year: number, month: number) => 
    apiClient.get<ApiResponse<MonthlySummary>>(`/transactions/summary/monthly?year=${year}&month=${month}`),

  /**
   * Get user balance
   */
  getUserBalance: (userId: string) =>
    apiClient.get<ApiResponse<UserBalance>>(`/transactions/balance/${userId}`),

  /**
   * Bulk delete transactions (auto-moves Submitted to Draft first)
   */
  bulkDelete: (ids: string[]) =>
    apiClient.post<ApiResponse<BulkOperationResult>>('/transactions/bulk-delete', { ids }),

  /**
   * Bulk update transaction status
   */
  bulkStatusUpdate: (ids: string[], status: string) =>
    apiClient.post<ApiResponse<BulkOperationResult>>('/transactions/bulk-status-update', { ids, status }),

  /**
   * Bulk create split expense transactions (up to 50 per request).
   * Supports 'partial' (default) and 'fail-all' failure modes.
   */
  bulkCreate: (data: BulkCreateTransactionsRequest) =>
    apiClient.post<ApiResponse<BulkCreateResult>>('/transactions/bulk-create', data),

  /**
   * Bulk edit transactions (up to 50).
   * Each item specifies a transaction ID plus the fields to update.
   * Use failureMode='partial' (default) for per-item results, or
   * failureMode='fail-all' to roll back the entire batch on any failure.
   */
  bulkEdit: (data: BulkEditTransactionsRequest) =>
    apiClient.post<ApiResponse<BulkEditResult>>('/transactions/bulk-edit', data),
};

// ============================================================================
// Balances API
// ============================================================================

export const balancesApi = {
  /**
   * Get full balance summary (user balances, pairwise, grants)
   */
  getSummary: () =>
    apiClient.get<ApiResponse<BalanceSummary>>('/balances'),

  /**
   * Get all user balances
   */
  getUserBalances: () =>
    apiClient.get<ApiResponse<UserBalanceDetail[]>>('/balances/users'),

  /**
   * Get pairwise balances (who owes whom)
   */
  getPairwiseBalances: () =>
    apiClient.get<ApiResponse<PairwiseBalance[]>>('/balances/pairwise'),

  /**
   * Get grant utilization
   */
  getGrantUtilization: () =>
    apiClient.get<ApiResponse<GrantUtilization[]>>('/balances/grants'),

  /**
   * Get per-founder reimbursement position for one grant.
   * `pending` per founder is the cap the settlement endpoint enforces.
   */
  getGrantReimbursements: (grantId: string) =>
    apiClient.get<ApiResponse<GrantReimbursement[]>>(
      `/balances/grants/${grantId}/reimbursements`
    ),

  /**
   * Get detailed balance breakdown for a user
   */
  getUserBreakdown: (userId: string) =>
    apiClient.get<ApiResponse<UserBalanceBreakdown>>(`/balances/user/${userId}`),

  /**
   * Get daily balance history for a user
   */
  getBalanceHistory: (userId: string, startDate: string, endDate: string) =>
    apiClient.get<ApiResponse<BalanceHistoryPoint[]>>(`/balances/user/${userId}/history?startDate=${startDate}&endDate=${endDate}`),

  /**
   * Create a settlement
   */
  createSettlement: (data: CreateSettlementDto) =>
    apiClient.post<ApiResponse<Settlement>>('/balances/settlements', data),

  /**
   * Get settlements with pagination and filtering
   */
  getSettlements: (params?: SettlementQueryParams) =>
    apiClient.get<PaginatedResponse<Settlement>>(`/balances/settlements${buildQueryString(params || {})}`),

  /**
   * Get a settlement by ID
   */
  getSettlement: (id: string) =>
    apiClient.get<ApiResponse<Settlement>>(`/balances/settlements/${id}`),

  /**
   * Delete a settlement
   */
  deleteSettlement: (id: string) =>
    apiClient.delete<ApiResponse<Settlement>>(`/balances/settlements/${id}`),
};

// ============================================================================
// Dashboard API
// ============================================================================

export const dashboardApi = {
  /**
   * Get full dashboard data with all aggregations
   */
  getData: (params?: DashboardQueryParams) =>
    apiClient.get<ApiResponse<DashboardData>>(`/dashboard${buildQueryString(params || {})}`),

  /**
   * Get monthly spending trend
   */
  getSpendingTrend: (params?: DashboardQueryParams) =>
    apiClient.get<ApiResponse<MonthlySpendingPoint[]>>(`/dashboard/spending-trend${buildQueryString(params || {})}`),

  /**
   * Get category breakdown
   */
  getCategoryBreakdown: (params?: DashboardQueryParams) =>
    apiClient.get<ApiResponse<CategoryBreakdownItem[]>>(`/dashboard/category-breakdown${buildQueryString(params || {})}`),

  /**
   * Export transactions as CSV (returns blob)
   */
  exportCsv: (params?: ExportQueryParams) =>
    apiClient.get(`/dashboard/export/csv${buildQueryString(params || {})}`, {
      responseType: 'blob',
    }),
};

// ============================================================================
// Receipts API
// ============================================================================

export const receiptsApi = {
  /**
   * Request upload token for a receipt
   */
  requestUploadToken: (data: UploadTokenRequest) => 
    apiClient.post<ApiResponse<UploadTokenResponse>>('/receipts/upload-token', data),

  /**
   * Confirm receipt upload
   */
  confirmUpload: (data: ConfirmUploadDto) => 
    apiClient.post<ApiResponse<Receipt>>('/receipts/confirm-upload', data),

  /**
   * Get receipts for a transaction
   */
  getByTransaction: (txnId: string) => 
    apiClient.get<ApiResponse<Receipt[]>>(`/receipts/transaction/${txnId}`),

  /**
   * Get a secure download URL for a receipt (SAS-signed)
   */
  getDownloadUrl: (id: string) =>
    apiClient.get<ApiResponse<{ downloadUrl: string; fileName: string | null }>>(`/receipts/${id}/download-url`),

  /**
   * Delete a receipt
   */
  delete: (id: string) =>
    apiClient.delete(`/receipts/${id}`),

  /**
   * Export matching receipts as a zip archive.
   * Returns a Blob response for client-side download.
   * @param params - Optional filter params: grantId, category, dateFrom, dateTo
   */
  exportZip: (params: {
    grantId?: string;
    category?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {}) =>
    apiClient.get(`/receipts/export.zip${buildQueryString(params)}`, {
      responseType: 'blob',
      timeout: 120000,
    }),
};

// ============================================================================
// API Keys API
// ============================================================================

export const apiKeysApi = {
  /**
   * Get all API keys for the current user
   */
  getAll: () => apiClient.get('/auth/api-keys'),

  /**
   * Create a new API key
   */
  create: (data: { name: string; expiresInDays: number | null; scopes?: string[] }) =>
    apiClient.post('/auth/api-keys', data),

  /**
   * Revoke an API key by ID
   */
  revoke: (id: string) => apiClient.delete(`/auth/api-keys/${id}`),
};

export default apiClient;

